<template>
  <div class="mt-8">
    <div class="mb-6 flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-semibold text-zinc-900">
          User Sessions
        </h2>
        <p class="mt-1 text-sm text-zinc-500">
          Login activity for creator admin and staff accounts.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 focus:border-[#2D8CFF] focus:outline-none active:bg-gray-100 active:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="loading || downloading"
          @click="downloadSessionLogs"
        >
          {{ downloading ? 'Preparing PDF...' : 'Download Logs' }}
        </button>
        <button
          type="button"
          class="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 focus:border-[#2D8CFF] focus:outline-none active:bg-gray-100 active:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="loading || downloading"
          @click="loadSessions"
        >
          {{ loading ? 'Refreshing...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
    >
      {{ errorMessage }}
    </div>

    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Location</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Device</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">I.P. Address</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Time</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <tr v-if="!loading && sessions.length === 0">
            <td
              colspan="5"
              class="px-4 py-4 text-sm text-gray-500"
            >
              No login sessions found.
            </td>
          </tr>
          <tr
            v-for="session in orderedSessions"
            :key="session.sessionUid"
            class="hover:bg-gray-50"
          >
            <td class="px-4 py-2 text-sm text-gray-900">
              <div class="flex min-w-[240px] items-center gap-3">
                <div class="h-10 w-10 overflow-hidden rounded-full bg-gray-100">
                  <img
                    v-if="!failedDefaultAvatarIds.has(session.sessionUid)"
                    :src="failedProfileAvatarIds.has(session.sessionUid) || !session.profilePic ? defaultAvatarSrc : session.profilePic"
                    alt="User avatar"
                    class="h-10 w-10 object-cover"
                    @error="handleAvatarError(session)"
                  >
                  <div
                    v-else
                    class="flex h-10 w-10 items-center justify-center text-xs font-semibold text-gray-500"
                  >
                    {{ initialsFor(session.displayName || session.email) }}
                  </div>
                </div>
                <div class="min-w-0">
                  <p class="truncate font-semibold text-gray-900">
                    {{ session.displayName || 'Unknown User' }}
                  </p>
                  <p class="truncate text-xs text-gray-500">
                    {{ session.email || '-' }}
                  </p>
                  <div class="mt-1 flex flex-wrap items-center gap-1.5">
                    <span class="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium text-zinc-600">
                      {{ roleLabel(session) }}
                    </span>
                    <span
                      v-if="session.isCurrentSession"
                      class="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                    >
                      Current
                    </span>
                  </div>
                </div>
              </div>
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              {{ session.locationLabel || 'Unknown location' }}
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              <div class="flex items-center gap-2">
                <component
                  :is="iconForDevice(session.deviceType)"
                  :class="iconClassForDevice(session.deviceType)"
                  class="h-6 w-6"
                />
                <div>
                  <p>{{ session.deviceName || 'Unknown device' }}</p>
                  <p class="text-xs text-gray-500">
                    {{ session.browserName || 'Unknown browser' }} - {{ session.osName || 'Unknown OS' }}
                  </p>
                </div>
              </div>
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              {{ session.ipAddress || '-' }}
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              <div class="space-y-1">
                <p>{{ formatDateTime(session.loginAt) }}</p>
                <p class="text-xs text-gray-500">
                  {{ session.status === 'active' ? 'Active' : `Ended${session.logoutAt ? ` - ${formatDateTime(session.logoutAt)}` : ''}` }}
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mx-2 mb-6 flex items-center justify-between border-t py-5">
      <div class="text-sm text-gray-700">
        Showing
        <span class="font-medium">{{ startIndex }}</span>
        to
        <span class="font-medium">{{ endIndex }}</span>
        of
        <span class="font-medium">{{ pagination.total }}</span>
        results
      </div>
      <div class="flex items-center gap-4">
        <div class="text-sm text-gray-700">
          Page
          <span class="font-medium">{{ pagination.page }}</span>
          of
          <span class="font-medium">{{ totalPages }}</span>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-[10px] py-[6px] text-xs font-medium text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 focus:border-[#2D8CFF] focus:outline-none active:bg-gray-100 active:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="loading || pagination.page <= 1"
            @click="goToPage(pagination.page - 1)"
          >
            Previous
          </button>
          <button
            type="button"
            class="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-[10px] py-[6px] text-xs font-medium text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 focus:border-[#2D8CFF] focus:outline-none active:bg-gray-100 active:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="loading || pagination.page >= totalPages"
            @click="goToPage(pagination.page + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { ComputerDesktopIcon, DevicePhoneMobileIcon, DeviceTabletIcon } from '@heroicons/vue/24/outline';
import defaultAvatarSrc from '@/assets/media/images/default-avatar.png';

const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').trim().replace(/\/+$/, '');
const apiBackendUrl = String(process.env.VUE_APP_PLATFORMA_API_BACKEND_URL || '').trim().replace(/\/+$/, '');
const PAGE_SIZE = 25;

const loading = ref(false);
const downloading = ref(false);
const errorMessage = ref('');
const sessions = ref([]);
const failedProfileAvatarIds = ref(new Set());
const failedDefaultAvatarIds = ref(new Set());
const handleAvatarError = (session) => {
  const uid = session?.sessionUid;
  if (!uid) return;
  if (session.profilePic && !failedProfileAvatarIds.value.has(uid)) {
    failedProfileAvatarIds.value = new Set([...failedProfileAvatarIds.value, uid]);
  } else {
    failedDefaultAvatarIds.value = new Set([...failedDefaultAvatarIds.value, uid]);
  }
};
const pagination = ref({
  page: 1,
  limit: PAGE_SIZE,
  total: 0,
  totalPages: 1,
});

const withApiBase = (pathValue) => {
  const normalizedPath = String(pathValue || '').startsWith('/')
    ? String(pathValue || '')
    : `/${String(pathValue || '')}`;
  return apiProxyUrl ? `${apiProxyUrl}${normalizedPath}` : normalizedPath;
};

const withBackendBase = (pathValue) => {
  const normalizedPath = String(pathValue || '').startsWith('/')
    ? String(pathValue || '')
    : `/${String(pathValue || '')}`;
  return apiBackendUrl ? `${apiBackendUrl}${normalizedPath}` : normalizedPath;
};

const buildAliasPath = (pathValue) => {
  const normalizedPath = String(pathValue || '').startsWith('/')
    ? String(pathValue || '')
    : `/${String(pathValue || '')}`;
  if (normalizedPath.startsWith('/api/creators/staff')) {
    return normalizedPath.replace('/api/creators/staff', '/api/creator-staff');
  }
  return normalizedPath;
};

const buildCandidateUrls = (pathValue) => {
  const normalizedPath = String(pathValue || '').startsWith('/')
    ? String(pathValue || '')
    : `/${String(pathValue || '')}`;
  const aliasPath = buildAliasPath(normalizedPath);
  const urls = [];
  const push = (value) => {
    const text = String(value || '').trim();
    if (!text || urls.includes(text)) return;
    urls.push(text);
  };
  push(withApiBase(normalizedPath));
  push(withApiBase(aliasPath));
  push(withBackendBase(normalizedPath));
  push(withBackendBase(aliasPath));
  push(normalizedPath);
  push(aliasPath);
  return urls;
};

const parsePayload = async (response) => {
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    return response.json().catch(() => ({}));
  }
  const text = await response.text().catch(() => '');
  return { __rawText: text };
};

const parseDownloadFilename = (contentDisposition, fallbackName) => {
  const header = String(contentDisposition || '');
  if (!header) return fallbackName;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(String(utf8Match[1]).replace(/^"|"$/g, ''));
    } catch (_error) {
      return fallbackName;
    }
  }

  const asciiMatch = header.match(/filename="?([^";]+)"?/i);
  if (asciiMatch?.[1]) return String(asciiMatch[1]).trim();
  return fallbackName;
};

const triggerDownload = (blob, fileName) => {
  if (!(blob instanceof Blob)) throw new Error('Invalid report file');
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = String(fileName || 'account-login-sessions-report.pdf');
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
};

const fetchSessionsApi = async ({ page = 1, limit = PAGE_SIZE } = {}) => {
  const basePath = `/api/creators/staff/sessions?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`;
  const urls = buildCandidateUrls(basePath);

  let lastError = new Error('Failed to load sessions');
  for (const url of urls) {
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });
      const payload = await parsePayload(response);
      const rawText = String(payload?.__rawText || '').toLowerCase();
      const routeMiss = rawText.includes('<!doctype html') && rawText.includes('cannot get');
      if ((!response.ok || payload?.success === false) && routeMiss) {
        continue;
      }
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || `Failed to load sessions (${response.status})`);
      }
      return payload;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
};

const loadSessions = async ({ page = pagination.value.page } = {}) => {
  loading.value = true;
  errorMessage.value = '';
  try {
    const payload = await fetchSessionsApi({ page, limit: PAGE_SIZE });
    sessions.value = Array.isArray(payload?.sessions) ? payload.sessions : [];
    failedProfileAvatarIds.value = new Set();
    failedDefaultAvatarIds.value = new Set();
    pagination.value = {
      page: Number(payload?.pagination?.page || page || 1),
      limit: Number(payload?.pagination?.limit || PAGE_SIZE),
      total: Number(payload?.pagination?.total || 0),
      totalPages: Number(payload?.pagination?.totalPages || 1),
    };
  } catch (error) {
    errorMessage.value = error?.message || 'Failed to load user sessions';
  } finally {
    loading.value = false;
  }
};

const downloadSessionLogs = async () => {
  downloading.value = true;
  errorMessage.value = '';
  try {
    const basePath = '/api/creators/staff/sessions/download';
    const urls = buildCandidateUrls(basePath);

    let lastError = 'Failed to download account sessions report';
    for (const url of urls) {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        if (!blob || !blob.size) {
          lastError = 'Received an empty report file';
          continue;
        }
        const fallbackName = 'account-login-sessions.pdf';
        const fileName = parseDownloadFilename(response.headers.get('content-disposition'), fallbackName);
        triggerDownload(blob, fileName);
        return;
      }

      const payload = await parsePayload(response);
      const rawText = String(payload?.__rawText || '').toLowerCase();
      const routeMiss = rawText.includes('<!doctype html') && rawText.includes('cannot get');
      if (routeMiss) {
        continue;
      }
      lastError = String(payload?.message || payload?.error || `Failed to download report (${response.status})`);
      if (response.status !== 404) break;
    }

    throw new Error(lastError);
  } catch (error) {
    errorMessage.value = error?.message || 'Failed to download account sessions report';
  } finally {
    downloading.value = false;
  }
};

const goToPage = async (nextPage) => {
  const page = Math.max(1, Number(nextPage || 1));
  await loadSessions({ page });
};

const totalPages = computed(() => Math.max(1, Number(pagination.value.totalPages || 1)));
const orderedSessions = computed(() => (
  [...sessions.value].sort((a, b) => {
    const aTs = a?.loginAt ? new Date(a.loginAt).getTime() : 0;
    const bTs = b?.loginAt ? new Date(b.loginAt).getTime() : 0;
    return bTs - aTs;
  })
));
const startIndex = computed(() => {
  if (Number(pagination.value.total || 0) <= 0) return 0;
  return ((Number(pagination.value.page || 1) - 1) * Number(pagination.value.limit || PAGE_SIZE)) + 1;
});
const endIndex = computed(() => {
  if (Number(pagination.value.total || 0) <= 0) return 0;
  return Math.min(
    Number(pagination.value.total || 0),
    Number(startIndex.value || 0) + Number(pagination.value.limit || PAGE_SIZE) - 1
  );
});

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const initialsFor = (value) => {
  const text = String(value || '').trim();
  if (!text) return 'U';
  const parts = text.split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
  return initials || 'U';
};

const iconForDevice = (deviceType) => {
  const normalized = String(deviceType || '').toLowerCase();
  if (normalized === 'mobile') return DevicePhoneMobileIcon;
  if (normalized === 'tablet') return DeviceTabletIcon;
  return ComputerDesktopIcon;
};

const iconClassForDevice = (deviceType) => {
  const normalized = String(deviceType || '').toLowerCase();
  if (normalized === 'mobile') return 'text-purple-500';
  if (normalized === 'tablet') return 'text-teal-500';
  return 'text-blue-500';
};

const roleLabel = (session) => {
  const role = String(session?.roleLabel || '').toLowerCase();
  if (role === 'admin') return 'Admin';
  if (role === 'staff') return 'Staff';
  return String(session?.userType || '').toLowerCase() === 'creator_staff' ? 'Staff' : 'Admin';
};

onMounted(() => {
  loadSessions();
});
</script>
